import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LPP_COUNTRY_CODES } from "../data/regions";
import { ApplicationState } from "src/store";
import { PrivacyState } from "src/store/reducers/privacy-reducer";
import { EUROPE } from "../constants/privacyPolicyConstants";
import { PrivacyPolicyTabType, TabItem } from "../utils/privacyPolicyUtils";
import { getCountryDisplayName } from "../utils/regionUtils";

interface UsePrivacyPolicyTabsReturn {
	activeTab: string;
	activeTabType: PrivacyPolicyTabType;
	shouldShowTabs: boolean;
	hasLPP: boolean;
	tabItem: TabItem[];
	handleTabChange: (tabName: string) => void;
}

/**
 * Custom hook for managing privacy policy tabs (EU countries only)
 * Handles tab state, EU detection, and tab switching logic
 */
export const usePrivacyPolicyTabs = (): UsePrivacyPolicyTabsReturn => {
	const { t } = useTranslation();
	const globalTabName = t("PRIVACY_POLICY_GPP_TAB_GLOBAL");

	const privacyState = useSelector<ApplicationState, PrivacyState>(
		(state) => state.privacy
	);
	const ppRegion = privacyState.region;
	const ppLanguage = privacyState.language;

	// Get the selected privacy policy language display name
	const selectedPPLanguage = useMemo(() => {
		if (ppRegion?.toLowerCase() === EUROPE && ppLanguage) {
			return getCountryDisplayName(ppRegion, ppLanguage.split("-")[1]);
		}
		return "";
	}, [ppRegion, ppLanguage]);

	// Tab items for EU countries
	const tabItem: TabItem[] = useMemo(
		() => [{ name: globalTabName }, { name: selectedPPLanguage }],
		[globalTabName, selectedPPLanguage]
	);

	// Extract country code from ppLanguage
	const ppCountryCode = ppLanguage ? ppLanguage.split("-")[1] : "";

	// Check if country is EU (excluding Turkey)
	const hasLPP = LPP_COUNTRY_CODES.includes(ppCountryCode);

	// Determine if tabs should be shown (EU country excluding Turkey)
	const shouldShowTabs = useMemo(() => {
		return hasLPP;
	}, [ppRegion, hasLPP]);

	// Active tab state - default to Global tab for EU countries
	const [activeTab, setActiveTab] = useState<string>(globalTabName);
	const [activeTabType, setActiveTabType] =
		useState<PrivacyPolicyTabType>("global");

	// Update active tab when selectedPPLanguage changes (e.g., when user changes language)
	// Default to Global tab when language changes for EU countries
	useEffect(() => {
		if (selectedPPLanguage && shouldShowTabs) {
			setActiveTab(globalTabName);
			setActiveTabType("global");
		}
	}, [selectedPPLanguage, shouldShowTabs, globalTabName]);

	// Handle tab change
	const handleTabChange = useCallback(
		(tabName: string) => {
			const newTabType = tabName === globalTabName ? "global" : "local";
			setActiveTab(tabName);
			setActiveTabType(newTabType);
		},
		[globalTabName]
	);

	return {
		activeTab,
		activeTabType,
		shouldShowTabs,
		hasLPP,
		tabItem,
		handleTabChange,
	};
};
